import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiPostJson } from './api';

const PRIMARY = '#1993e5';

interface ChatMessage {
  id: string;
  sender: 'user' | 'ai';
  content: string;
  timestamp: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle?: string;
  reportContent: string;
  workflowType?: string;
  customerId?: string;
}

// Strip `[CITE: "..."]` markers and the desktop's 📑 citation buttons from
// AI responses — mobile has no PDF-jump affordance.
const stripCitations = (s: string): string =>
  s
    .replace(/\[CITE:\s*"(?:[^"\\]|\\.)*"\s*\]/g, '')
    .replace(/📑/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const SUGGESTED = [
  'Summarize this report in 3 bullets.',
  'What are the biggest risks?',
  'What changed vs prior period?',
  'Any coverage gaps?',
];

interface WorkflowChatResponse {
  success?: boolean;
  response?: string;
  error?: string;
  sessionId?: string;
}

const MobileReportChat: React.FC<Props> = ({
  open, onClose, reportId, reportTitle, reportContent, workflowType, customerId,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Clear chat state whenever a different report is opened.
  useEffect(() => {
    if (open) {
      setMessages([]);
      setSessionId(undefined);
      setError(null);
      setInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, reportId]);

  // Keep the conversation pinned to the bottom as messages arrive.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Lock body scroll while the sheet is open so the iOS rubber-band scroll
  // doesn't surface the report behind it.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const userMsg: ChatMessage = {
      id: newId(),
      sender: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };
    const isFirstMessage = messages.length === 0;
    const history = messages.map((m) => ({
      sender: m.sender,
      content: m.content,
      timestamp: m.timestamp,
    }));

    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    setError(null);

    try {
      const payload = {
        message: trimmed,
        reportId,
        reportContent,
        reportMetadata: {
          title: reportTitle || 'Report',
          workflowType: workflowType || 'report',
        },
        documents: [],
        chatHistory: history,
        includeReport: true,
        sessionId,
        customerId,
        isFirstMessage,
      };

      const resp = await apiPostJson<WorkflowChatResponse>('/chat/workflow', payload);

      if (resp.sessionId && !sessionId) setSessionId(resp.sessionId);

      const aiText = resp.response || resp.error || 'No response received.';
      const aiMsg: ChatMessage = {
        id: newId(),
        sender: 'ai',
        content: stripCitations(aiText),
        timestamp: new Date().toISOString(),
      };
      setMessages((m) => [...m, aiMsg]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Chat request failed';
      setError(msg);
      // Append an inline error bubble so the user sees what failed inline
      // rather than having to scroll up to a banner.
      setMessages((m) => [
        ...m,
        { id: newId(), sender: 'ai', content: `⚠️ ${msg}`, timestamp: new Date().toISOString() },
      ]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-950">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-3 dark:border-slate-700 dark:bg-slate-900">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: PRIMARY }}>
            Ask AI
          </div>
          <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
            {reportTitle || 'Report'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="-mr-1 flex h-9 w-9 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
            <path d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z" />
          </svg>
        </button>
      </header>

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-3 py-3"
        style={{ paddingBottom: '0.5rem' }}
      >
        {messages.length === 0 && !sending && (
          <div className="mt-2 space-y-3">
            <div className="rounded-xl bg-white p-3 text-sm text-slate-600 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
              Ask anything about this report. I have the full report as context, so I can summarize, compare, or pull out specific facts.
            </div>
            <div className="grid grid-cols-1 gap-2">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 active:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:active:bg-slate-800"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <ul className="space-y-2">
          {messages.map((m) => {
            const isUser = m.sender === 'user';
            return (
              <li key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] break-words rounded-2xl px-3 py-2 text-sm ${
                    isUser
                      ? 'whitespace-pre-wrap text-white'
                      : 'bg-white text-slate-800 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700'
                  }`}
                  style={isUser ? { backgroundColor: PRIMARY } : undefined}
                >
                  {isUser ? (
                    m.content
                  ) : (
                    <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1.5 prose-p:text-slate-800 dark:prose-p:text-slate-100 prose-li:my-0.5 prose-li:text-slate-800 dark:prose-li:text-slate-100 prose-ul:my-1.5 prose-ol:my-1.5 prose-strong:text-slate-900 dark:prose-strong:text-white prose-a:text-primary prose-code:rounded prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none dark:prose-code:bg-slate-800 prose-pre:my-2 prose-pre:rounded-lg prose-pre:bg-slate-900 prose-pre:p-2 prose-pre:text-xs prose-pre:text-slate-100 prose-table:text-xs prose-th:bg-slate-100 dark:prose-th:bg-slate-800 prose-th:px-1.5 prose-th:py-1 prose-td:px-1.5 prose-td:py-1 prose-th:border prose-td:border prose-th:border-slate-200 prose-td:border-slate-200 dark:prose-th:border-slate-700 dark:prose-td:border-slate-700"
                         style={{ ['--tw-prose-links' as any]: PRIMARY }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          {sending && (
            <li className="flex justify-start">
              <div className="rounded-2xl bg-white px-3 py-2 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                <span className="inline-flex gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full" style={{ backgroundColor: PRIMARY, animationDelay: '0ms' }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full" style={{ backgroundColor: PRIMARY, animationDelay: '150ms' }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full" style={{ backgroundColor: PRIMARY, animationDelay: '300ms' }} />
                </span>
              </div>
            </li>
          )}
        </ul>
      </div>

      {error && messages.length === 0 && (
        <div className="mx-3 mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="flex items-end gap-2 border-t border-slate-200 bg-white px-3 py-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] dark:border-slate-700 dark:bg-slate-900"
      >
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            // Grow up to ~5 lines
            const ta = e.target;
            ta.style.height = 'auto';
            ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder="Ask about this report…"
          className="max-h-36 min-h-[40px] flex-1 resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          aria-label="Send"
          className="flex h-10 w-10 items-center justify-center rounded-full text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          style={{ backgroundColor: PRIMARY }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
            <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default MobileReportChat;
